import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import ScheduleViewerClock, {
  ScheduleNowLine,
  useScheduleNowTick,
} from '../components/ScheduleViewerClock';
import {
  WEEKDAY_FULL,
  WEEKDAY_NAMES,
  addDaysISO,
  formatDateShort,
  formatDualTime,
  formatDualTimeFromTemplate,
  formatDualTimeOnDate,
  loadViewerTzPreference,
  minutesFromMidnight,
  mondayOf,
  moscowDateISO,
  nowLineTopPx,
  occurrenceDateMoscow,
  timeFromOccurrenceStart,
  weekDates,
} from '../utils/scheduleDates';
import { useAuth } from '../context/AuthContext';

const HOUR_HEIGHT = 44;

function occStyle(occ, gridStartMin) {
  const startMin = minutesFromMidnight(timeFromOccurrenceStart(occ.start));
  const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
  const height = Math.max((occ.duration_minutes / 60) * HOUR_HEIGHT, 22);
  return { top: `${top}px`, height: `${height}px` };
}

/** Раскладка пересекающихся отрезков по колонкам внутри группы. */
function assignLanes(items) {
  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || a.id - b.id
  );
  const laneEnds = [];
  const withLane = sorted.map((item) => {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.startMin) lane += 1;
    if (lane === laneEnds.length) laneEnds.push(0);
    laneEnds[lane] = item.endMin;
    return { ...item, lane };
  });
  const laneCount = Math.max(1, ...withLane.map((x) => x.lane + 1));
  return withLane.map((x) => ({ ...x, laneCount }));
}

function studentBarStyle(startTime, durationMinutes, windowStartTime, lane, laneCount) {
  const startMin = minutesFromMidnight((startTime || '00:00').slice(0, 5));
  const winStart = minutesFromMidnight((windowStartTime || '00:00').slice(0, 5));
  const top = ((startMin - winStart) / 60) * HOUR_HEIGHT;
  const height = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 20);
  const widthPct = 100 / laneCount;
  return {
    top: `${top}px`,
    height: `${height}px`,
    left: `calc(${lane * widthPct}% + 2px)`,
    width: `calc(${widthPct}% - 4px)`,
    right: 'auto',
  };
}

export default function MySchedulePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqBusyKey, setReqBusyKey] = useState('');
  const [reqNote, setReqNote] = useState({});
  const [reqProposed, setReqProposed] = useState({});
  const [reqError, setReqError] = useState('');
  const [reqOk, setReqOk] = useState('');
  const [weekStart, setWeekStart] = useState(() => mondayOf(moscowDateISO()));
  const [viewerTz, setViewerTz] = useState(() => loadViewerTzPreference());
  const now = useScheduleNowTick();

  const studentId = searchParams.get('student_id') || '';

  useEffect(() => {
    if (!user?.is_parent) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/parent/children/');
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
          setChildren(list);
        }
      } catch {
        if (!cancelled) setChildren([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = { week: weekStart };
        if (studentId) params.student_id = studentId;
        const [sched, changeReqs] = await Promise.all([
          api.get('/scheduler/me/', { params }),
          api.get('/scheduler/me/change-requests/', { params }).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setData(sched.data);
          setRequests(Array.isArray(changeReqs.data) ? changeReqs.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Не удалось загрузить расписание');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, weekStart]);

  async function submitChangeRequest(occ, kind) {
    setReqError('');
    setReqOk('');
    setReqBusyKey(`${occ.key}:${kind}`);
    try {
      const payload = {
        teacher_id: occ.teacher_id,
        occurrence_key: occ.key,
        occurrence_start: occ.start,
        weekly_slot_id: occ.weekly_slot_id || null,
        oneoff_id: occ.oneoff_id || null,
        original_date: occ.original_date || occurrenceDateMoscow(occ.start),
        kind,
        note: (reqNote[occ.key] || '').trim(),
      };
      if (studentId) payload.student_id = Number(studentId);
      if (kind === 'reschedule') {
        const proposed = (reqProposed[occ.key] || '').trim();
        if (!proposed) {
          setReqError('Укажите желаемое новое время (локальное, например 2026-09-12T17:00).');
          return;
        }
        payload.proposed_new_start = proposed;
      }
      const res = await api.post('/scheduler/me/change-requests/', payload);
      setRequests((prev) => [res.data, ...prev]);
      setReqOk(kind === 'cancel' ? 'Запрос на отмену отправлен' : 'Запрос на перенос отправлен');
    } catch (err) {
      setReqError(err.response?.data?.detail || 'Не удалось отправить запрос');
    } finally {
      setReqBusyKey('');
    }
  }

  const today = moscowDateISO(now);
  const upcoming = (data?.occurrences || []).filter((o) => !o.is_cancelled);
  const cancelled = (data?.occurrences || []).filter((o) => o.is_cancelled);
  const weekly = data?.weekly_pattern || [];
  const pendingKeys = new Set(
    requests.filter((r) => r.status === 'pending').map((r) => r.occurrence_key),
  );

  const calendar = data?.calendar;
  const workStart = calendar?.work_start || '11:00';
  const workEnd = calendar?.work_end || '21:00';
  const gridStartMin = minutesFromMidnight(workStart);
  const gridEndMin = Math.max(minutesFromMidnight(workEnd), gridStartMin + 60);
  const gridHours = Math.ceil((gridEndMin - gridStartMin) / 60);
  const days = useMemo(() => weekDates(weekStart), [weekStart]);
  const occByDay = useMemo(() => {
    const map = Object.fromEntries(days.map((d) => [d, []]));
    for (const occ of calendar?.occurrences || []) {
      const d = occurrenceDateMoscow(occ.start);
      if (map[d]) map[d].push(occ);
    }
    return map;
  }, [calendar, days]);
  const hourLabels = Array.from({ length: gridHours + 1 }, (_, i) => {
    const mins = gridStartMin + i * 60;
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:00`;
  });
  const nowLineTop = useMemo(
    () =>
      nowLineTopPx({
        now,
        workStart,
        workEnd,
        hourHeight: HOUR_HEIGHT,
        todayIso: today,
      }),
    [now, workStart, workEnd, today],
  );

  function occClass(occ) {
    const parts = ['sched-occ', 'sched-occ--readonly'];
    if (occ.is_cancelled) parts.push('sched-occ--cancelled');
    if (occ.is_group) parts.push('sched-occ--group');
    if (occ.is_mine) parts.push('sched-occ--mine');
    else parts.push('sched-occ--other');
    if (occ.on_holiday) parts.push('sched-occ--holiday');
    if (occ.kind === 'oneoff') parts.push('sched-occ--oneoff');
    if (occ.kind === 'rescheduled') parts.push('sched-occ--moved');
    return parts.join(' ');
  }

  return (
    <div className="sched-page sched-page--student">
      <div className="page-header">
        <h1>Моё расписание</h1>
        <p className="page-subtitle">
          Календарь кабинета (только просмотр) и списки своих занятий. Прошедшие — в{' '}
          <Link to="/my-lessons">журнале занятий</Link>
          {' · '}
          <Link to={studentId ? `/my-messages?student_id=${studentId}` : '/my-messages'}>
            сообщения
          </Link>
          .
        </p>
        {(data?.telemost_url || user?.telemost_url) && (
          <p style={{ marginTop: '0.75rem' }}>
            <a
              className="btn btn-primary"
              href={data?.telemost_url || user.telemost_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa-solid fa-video" aria-hidden />
              {' '}
              Войти в Телемост
            </a>
          </p>
        )}
      </div>

      {user?.is_parent && children.length > 0 && (
        <label className="sched-filter">
          Ребёнок
          <select
            value={studentId}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setSearchParams({ student_id: v });
              else setSearchParams({});
            }}
          >
            <option value="">По умолчанию</option>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name || c.first_name || c.username}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Загрузка…</p>}

      {!loading && data && (
        <>
          {data.exam && data.exam.exam_date && (
            <div className="sched-exam-banner">
              До экзамена {data.exam.exam_date}: <strong>{data.exam.count}</strong> занятий
              {data.exam.holiday_hits?.length > 0 && (
                <span className="muted">
                  {' '}
                  (+{data.exam.holiday_hits.length} на праздники — уточните у преподавателя)
                </span>
              )}
            </div>
          )}

          <section className="sched-panel">
            <div className="sched-toolbar">
              <div className="sched-week-nav">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setWeekStart(addDaysISO(weekStart, -7))}
                >
                  ← Неделя
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setWeekStart(mondayOf(moscowDateISO()))}
                >
                  Сегодня
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setWeekStart(addDaysISO(weekStart, 7))}
                >
                  Неделя →
                </button>
                <span className="sched-week-label">
                  {formatDateShort(weekStart)} — {formatDateShort(addDaysISO(weekStart, 6))}
                </span>
              </div>
              <ScheduleViewerClock
                showTimezoneSelect
                viewerTz={viewerTz}
                onViewerTzChange={setViewerTz}
                now={now}
              />
            </div>
            <h2>Календарь</h2>
            <p className="muted sched-panel-hint">
              Неделя кабинета преподавателя. <span className="sched-legend-swatch sched-legend-swatch--mine" /> ваши
              занятия, <span className="sched-legend-swatch sched-legend-swatch--group" /> группы (внутри — отрезки
              без имён). Чужие индивидуальные — «занято». Только просмотр.
            </p>
            <div className="sched-grid-wrap">
              <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
                <div className="sched-grid-corner" />
                {days.map((d, i) => (
                  <div key={d} className={`sched-day-head${d === today ? ' sched-day-head--today' : ''}`}>
                    <div className="sched-day-name">{WEEKDAY_NAMES[i]}</div>
                    <div className="sched-day-date">{formatDateShort(d)}</div>
                  </div>
                ))}
                <div className="sched-hours">
                  {hourLabels.map((label) => (
                    <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                      {label}
                    </div>
                  ))}
                </div>
                {days.map((d) => (
                  <div
                    key={d}
                    className={`sched-day-col${d === today ? ' sched-day-col--today' : ''}`}
                    style={{ height: gridHours * HOUR_HEIGHT }}
                  >
                    {hourLabels.slice(0, -1).map((label) => (
                      <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                    ))}
                    {d === today && <ScheduleNowLine topPx={nowLineTop} />}
                    {(occByDay[d] || []).map((occ) => {
                      const members = occ.members || [];
                      const showGroupBars =
                        occ.is_group && !occ.is_cancelled && members.length > 0;
                      if (showGroupBars) {
                        const windowStart = timeFromOccurrenceStart(occ.start);
                        const bars = assignLanes(
                          members.map((m, idx) => {
                            const start = (m.start_time || windowStart).slice(0, 5);
                            const dur = m.duration_minutes || occ.duration_minutes || 60;
                            const startMin = minutesFromMidnight(start);
                            return {
                              id: idx,
                              start,
                              dur,
                              startMin,
                              endMin: startMin + dur,
                              isMe: Boolean(m.is_me),
                            };
                          })
                        );
                        return (
                          <div key={occ.key} className="sched-group-wrap" style={occStyle(occ, gridStartMin)}>
                            <div
                              className={`sched-occ sched-occ--readonly sched-occ--group-window${
                                occ.on_holiday ? ' sched-occ--holiday' : ''
                              }${occ.is_mine ? '' : ' sched-occ--other'}`}
                              style={{ top: 0, height: '100%', left: 0, right: 0 }}
                              title={occ.label}
                            >
                              <span className="sched-occ-people">
                                <span className="sched-occ-person sched-occ-person--empty">
                                  группа {occ.enrolled_count || bars.length}
                                </span>
                              </span>
                              <span className="sched-occ-meta">
                                <span className="sched-occ-time">{formatDualTime(occ.start, viewerTz)}</span>
                              </span>
                            </div>
                            {bars.map((bar) => {
                              const title = bar.isMe
                                ? `я ${bar.start} · ${bar.dur} мин`
                                : `${bar.start} · ${bar.dur} мин`;
                              return (
                                <div
                                  key={bar.id}
                                  className={`sched-occ sched-occ--readonly sched-occ--member${
                                    bar.isMe ? ' sched-occ--mine' : ''
                                  }`}
                                  style={studentBarStyle(
                                    bar.start,
                                    bar.dur,
                                    windowStart,
                                    bar.lane,
                                    bar.laneCount
                                  )}
                                  title={title}
                                >
                                  {bar.isMe && (
                                    <span className="sched-occ-people">
                                      <span className="sched-occ-person">я</span>
                                    </span>
                                  )}
                                  <span className="sched-occ-meta">
                                    <span className="sched-occ-time">
                                      {formatDualTimeOnDate(d, bar.start, viewerTz)}
                                    </span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      return (
                        <div
                          key={occ.key}
                          className={occClass(occ)}
                          style={occStyle(occ, gridStartMin)}
                          title={occ.label}
                        >
                          <span className="sched-occ-people">
                            <span className="sched-occ-person">{occ.label}</span>
                          </span>
                          <span className="sched-occ-meta">
                            <span className="sched-occ-time">{formatDualTime(occ.start, viewerTz)}</span>
                            {occ.is_group && (
                              <span className="sched-occ-slots">{occ.enrolled_count}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="sched-panel">
            <h2>Обычное расписание</h2>
            <p className="muted">Постоянные слоты каждую неделю (без разовых отмен).</p>
            {weekly.length === 0 ? (
              <p className="muted">Пока нет постоянного слота.</p>
            ) : (
              <ul className="sched-upcoming-list">
                {weekly.map((slot) => (
                  <li key={`${slot.teacher_id}-${slot.id}`}>
                    <div className="sched-upcoming-date">
                      {WEEKDAY_FULL[slot.weekday]},{' '}
                      {formatDualTimeFromTemplate(slot.start_time, slot.weekday, viewerTz)}
                    </div>
                    <div className="sched-upcoming-meta">
                      {slot.duration_minutes} мин
                      {slot.is_group ? ` · группа` : ''}
                      {slot.teacher_name ? ` · ${slot.teacher_name}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="sched-panel">
            <h2>Ближайшие даты</h2>
            <p className="muted">
              Конкретные занятия с {data.from} по {data.to}, с учётом отмен и переносов.
              Перенос или отмену можно запросить кнопками — преподаватель подтвердит в очереди.
            </p>
            {reqError && <div className="alert alert-error">{reqError}</div>}
            {reqOk && <div className="alert alert-success">{reqOk}</div>}
            {upcoming.length === 0 ? (
              <p className="muted">В этом периоде нет запланированных занятий.</p>
            ) : (
              <ul className="sched-upcoming-list">
                {upcoming.map((occ) => {
                  const d = occurrenceDateMoscow(occ.start);
                  const isToday = d === today;
                  const pending = pendingKeys.has(occ.key);
                  return (
                    <li key={occ.key} className={isToday ? 'is-today' : ''}>
                      <div className="sched-upcoming-date">
                        {isToday ? 'Сегодня' : formatDateShort(d)}
                        {', '}
                        {formatDualTime(occ.start, viewerTz)}
                        {occ.kind === 'rescheduled' ? ' · перенос' : ''}
                        {occ.kind === 'oneoff' ? ' · разовое' : ''}
                      </div>
                      <div className="sched-upcoming-meta">
                        {occ.duration_minutes} мин
                        {occ.is_group ? ` · группа` : ''}
                        {occ.teacher_name ? ` · ${occ.teacher_name}` : ''}
                        {occ.on_holiday ? ` · ${occ.holiday_name || 'праздник'}` : ''}
                      </div>
                      <div className="ops-change-actions">
                        {pending ? (
                          <span className="muted">Запрос уже в очереди у преподавателя</span>
                        ) : (
                          <>
                            <input
                              className="form-input ops-change-note"
                              type="text"
                              placeholder="Комментарий (необязательно)"
                              value={reqNote[occ.key] || ''}
                              onChange={(e) => setReqNote((prev) => ({ ...prev, [occ.key]: e.target.value }))}
                            />
                            <input
                              className="form-input ops-change-note"
                              type="text"
                              placeholder="Новое время ISO, напр. 2026-09-12T17:00"
                              value={reqProposed[occ.key] || ''}
                              onChange={(e) => setReqProposed((prev) => ({ ...prev, [occ.key]: e.target.value }))}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              disabled={!!reqBusyKey}
                              onClick={() => submitChangeRequest(occ, 'cancel')}
                            >
                              {reqBusyKey === `${occ.key}:cancel` ? '…' : 'Отменить'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              disabled={!!reqBusyKey}
                              onClick={() => submitChangeRequest(occ, 'reschedule')}
                            >
                              {reqBusyKey === `${occ.key}:reschedule` ? '…' : 'Перенести'}
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {requests.length > 0 && (
            <section className="sched-panel">
              <h2>Мои запросы</h2>
              <ul className="sched-upcoming-list">
                {requests.slice(0, 20).map((r) => (
                  <li key={r.id}>
                    <div className="sched-upcoming-date">
                      {r.kind === 'cancel' ? 'Отмена' : 'Перенос'} · {r.status}
                      {r.occurrence_start ? ` · ${formatDateShort(occurrenceDateMoscow(r.occurrence_start))} ${formatDualTime(r.occurrence_start, viewerTz)}` : ''}
                    </div>
                    <div className="sched-upcoming-meta">
                      {r.teacher_name}
                      {r.note ? ` · ${r.note}` : ''}
                      {r.teacher_note ? ` · ответ: ${r.teacher_note}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {cancelled.length > 0 && (
            <section className="sched-panel">
              <h2>Отмены в периоде</h2>
              <p className="muted">Разовые отмены или старое место после переноса — шаблон не менялся.</p>
              <ul className="sched-upcoming-list sched-upcoming-list--muted">
                {cancelled.map((occ) => (
                  <li key={occ.key}>
                    <div className="sched-upcoming-date">
                      {formatDateShort(occurrenceDateMoscow(occ.start))}, {formatDualTime(occ.start, viewerTz)} — отменено
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
